import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { formatCurrency, formatPercentage, formatNumber } from '@/lib/utils/format';
import type { PriceWaterfallData } from '../types/analytics.types';

const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  color: 'hsl(var(--foreground))',
};

interface WaterfallTabProps {
  data: PriceWaterfallData | null;
  isLoading: boolean;
}

interface WaterfallEntry {
  name: string;
  value: number;
  base: number;
  fill: string;
  isSubtotal: boolean;
}

export function WaterfallTab({ data, isLoading }: WaterfallTabProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card className="p-4">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-8 w-20" />
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-[400px] w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  // Build waterfall data: each bar has a base (invisible) and a value (visible)
  const waterfallEntries: WaterfallEntry[] = [
    {
      name: 'Costo Base',
      value: data.baseCost,
      base: 0,
      fill: '#3b82f6',
      isSubtotal: false,
    },
    {
      name: '+ Margen',
      value: data.margin,
      base: data.baseCost,
      fill: '#10b981',
      isSubtotal: false,
    },
    {
      name: '= Precio Lista',
      value: data.listPrice,
      base: 0,
      fill: '#6366f1',
      isSubtotal: true,
    },
    {
      name: '- Descuento',
      value: data.discount,
      base: data.listPrice - data.discount,
      fill: '#ef4444',
      isSubtotal: false,
    },
    {
      name: '= Precio Neto',
      value: data.netPrice,
      base: 0,
      fill: '#059669',
      isSubtotal: true,
    },
    {
      name: '+ Impuesto',
      value: data.tax,
      base: data.netPrice,
      fill: '#f59e0b',
      isSubtotal: false,
    },
  ];

  // For the stacked bar chart approach: use invisible base + visible segment
  const chartData = waterfallEntries.map((entry) => ({
    name: entry.name,
    base: entry.isSubtotal ? 0 : entry.base,
    value: entry.isSubtotal ? entry.value : entry.value,
    fill: entry.fill,
    total: entry.isSubtotal ? entry.value : entry.base + entry.value,
    displayValue: entry.value,
  }));

  // Summary KPIs
  const netPrice = data.netPrice;
  const marginPct = data.baseCost > 0 ? (data.margin / data.baseCost) * 100 : 0;
  const discountPct = data.listPrice > 0 ? (data.discount / data.listPrice) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Costo Base Promedio</p>
          <p className="text-2xl font-bold mt-1 text-blue-600">{formatCurrency(data.baseCost)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Margen</p>
          <p className="text-2xl font-bold mt-1 text-green-600">
            {formatCurrency(data.margin)}
            <span className="text-sm font-normal text-muted-foreground ml-1">
              ({formatPercentage(marginPct)})
            </span>
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Descuento</p>
          <p className="text-2xl font-bold mt-1 text-red-600">
            {formatCurrency(data.discount)}
            <span className="text-sm font-normal text-muted-foreground ml-1">
              ({formatPercentage(discountPct)})
            </span>
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Precio Neto Promedio</p>
          <p className="text-2xl font-bold mt-1 text-emerald-600">{formatCurrency(netPrice)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {formatNumber(data.quoteCount, 0)} cotizaciones
          </p>
        </Card>
      </div>

      {/* Waterfall Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Cascada de Precios (Promedio)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
                <YAxis
                  tick={{ fontSize: 12 }}
                  className="fill-muted-foreground"
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const entry = payload[0]?.payload;
                    if (!entry) return null;
                    return (
                      <div style={tooltipStyle} className="p-3 shadow-lg">
                        <p className="font-medium text-sm">{entry.name}</p>
                        <p className="text-sm">{formatCurrency(entry.displayValue)}</p>
                      </div>
                    );
                  }}
                />
                {/* Invisible base bar */}
                <Bar dataKey="base" stackId="waterfall" fill="transparent" />
                {/* Visible value bar */}
                <Bar dataKey="value" stackId="waterfall" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                  <LabelList
                    dataKey="displayValue"
                    position="top"
                    formatter={((v: any) => formatCurrency(Number(v))) as any}
                    className="fill-muted-foreground"
                    style={{ fontSize: 10 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
